import { AxiosResponse } from 'axios';
import { Action } from 'redux';

import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { FilterMap } from '~src/spacesPlatform/state/ContentExplorerState';
import { SpaceInfo, SpaceType } from '~src/spacesPlatform/state/SpaceInfo';

import {
  ClearDraftsState,
  DraftFetchByReferenceIdFailed,
  DraftFetchByReferenceIdRequested,
  DraftFetchByReferenceIdSucceeded,
  DraftsFetchInfoFailed,
  DraftsFetchInfoRequested,
  DraftsFetchInfoSucceeded,
  DraftsInfoAvailable
} from '../../bootstrap/redux/actions/AppActionTypes';
import { isActionFactory } from '../../bootstrap/redux/actions/isActionFactory';
import { GetDraftsByIdRequestParams } from '../requests/DraftsRequests';
import { ContentDraftStatus } from '../serverApi/types/ContentDraft';
import { ContainerType, Draft, DraftReferenceType, DraftsById, SpringResponse, WorkbookDraftContent, WorkbookDraftContentType } from '../types/DraftsTypes';
import { containerTypeToLocationType } from '../utils/DraftUtils';

export interface FetchDraftsSucceededAction extends Action {
  drafts: Draft[];
}

export interface DraftsInfoAvailableAction extends Action {
  contents: WorkbookDraftContent[];
}

export interface FetchDraftsFailedAction extends Action {
  error: AxiosResponse<SpringResponse>;
}

export interface ClearDraftsStateAction extends Action { }

export interface FetchDraftsRequestedAction extends Action {
  spaceInfo: SpaceInfo;
  filters: FilterMap;
  contentTypes: ReadonlyArray<ExplorableItemType>;
  shouldDispatchDraftActions: boolean;
}

export interface FetchDraftByReferenceIdSucceededAction extends Action {
  draft: Draft;
}

export interface FetchDraftByReferenceIdFailedAction extends Action {
  error: AxiosResponse<SpringResponse>;
}

export interface FetchDraftByReferenceIdRequestedAction extends Action {
  spaceType: SpaceType;
  referenceId: string;
  data: GetDraftsByIdRequestParams;
}

export function clearDraftState(): ClearDraftsStateAction {
  return {
    type: ClearDraftsState
  };
}

export function fetchDraftsRequested(
  spaceInfo: SpaceInfo,
  filters: FilterMap = {},
  contentTypes: ReadonlyArray<ExplorableItemType> = [],
  shouldDispatchDraftActions: boolean = true
): FetchDraftsRequestedAction {
  return {
    type: DraftsFetchInfoRequested,
    spaceInfo,
    filters,
    contentTypes,
    shouldDispatchDraftActions,
  };
}

export function fetchDraftsSucceeded(result: Draft[]): FetchDraftsSucceededAction {
  return {
    type: DraftsFetchInfoSucceeded,
    drafts: result,
  };
}

export function fetchDraftsFailed(error: AxiosResponse<SpringResponse>): FetchDraftsFailedAction {
  return {
    type: DraftsFetchInfoFailed,
    error,
  };
}

export function fetchDraftByReferenceIdRequested(spaceType: SpaceType, parentLuid: string, parentRevisionId?: string): FetchDraftByReferenceIdRequestedAction {
  return {
    type: DraftFetchByReferenceIdRequested,
    spaceType,
    referenceId: parentLuid,
    data: { parentRevisionId }
  };
}

export function fetchDraftByReferenceIdSucceeded(draft: Draft): FetchDraftByReferenceIdSucceededAction {
  return {
    type: DraftFetchByReferenceIdSucceeded,
    draft,
  };
}

export function fetchDraftByReferenceIdFailed(error: AxiosResponse<SpringResponse>): FetchDraftByReferenceIdFailedAction {
  return {
    type: DraftFetchByReferenceIdFailed,
    error
  };
}

export function draftsAvailable(result: Draft[]): DraftsInfoAvailableAction {
  return {
    type: DraftsInfoAvailable,
    contents: convertDraftsToWorkbookDraftContents(result),
  };
}

export function draftsAvailableInState(result: DraftsById): DraftsInfoAvailableAction {
  return {
    type: DraftsInfoAvailable,
    contents: convertDraftsToWorkbookDraftContents(Object.values(result)),
  };
}

// exported for testing
export function convertDraftsToWorkbookDraftContents(drafts: Draft[]): WorkbookDraftContent[] {
  if (!drafts) {
    return [];
  }

  const siteUrls = siteUrlsSelector(getAppStore().getState());
  const workbookDraftContents: WorkbookDraftContent[] = [];

  drafts.forEach(draft => {
    workbookDraftContents.push(convertDraftToWorkbookDraftContent(draft, siteUrls));
  });

  return workbookDraftContents;
}

// exported for testing
export function convertDraftToWorkbookDraftContent(draft: Draft, siteUrls: SpacesSiteUrls): WorkbookDraftContent {
  const draftStatus = getDraftStatus(draft);
  const displayName = SessionUserSelectors.sessionUserDisplayName(getAppStore().getState());
  const username = SessionUserSelectors.sessionUserName(getAppStore().getState());
  const location = draft.containerType === ContainerType.PersonalSpace ?
    {
      id: 'personalSpace',
      name: Messages.personalSpaceTitle(),
      type: containerTypeToLocationType(draft.containerType),
      luid: draft.containerLuid
    } : undefined;

  return {
    containerLuid: draft.containerLuid,
    containerType: draft.containerType,
    contentType: WorkbookDraftContentType,
    createdAt: new Date(draft.createdAt),
    draftLuid: draft.draftLuid,
    draftStatus,
    editUrl: getUnpublishedWorkbookEditUrl(draft, siteUrls),
    id: draft.draftReferenceId ?? draft.parentContentLuid,
    name: draft.name,
    thumbnailUrl: draft.thumbnailURL,
    updatedAt: new Date(draft.updatedAt),
    owner: {
      id: draft.userLuid,
      username: username,
      displayName: displayName,
      domainName: 'external',
      isHiddenInternalUser: false,
      readOnly: false
    },
    location: location,
  };
}

function getDraftStatus(draft: Draft): ContentDraftStatus {
  if (!draft.hasOwnProperty('draftReferenceType')) {
    return ContentDraftStatus.unpublishedchanges;
  }

  return draft.draftReferenceType === DraftReferenceType.UnpublishedWorkbook ? ContentDraftStatus.unpublished : ContentDraftStatus.unpublishedchanges;
}

// exported for testing
export function getUnpublishedWorkbookEditUrl(draft: Draft, siteUrls: SpacesSiteUrls): string {
  if (getDraftStatus(draft) !== ContentDraftStatus.unpublished) {
    return undefined;
  }

  const projectLuid = draft.containerType === ContainerType.Project ? draft.containerLuid : undefined;
  return siteUrls.unpublishedWorkbookUrl(draft.draftReferenceId, projectLuid);
}

export const isFetchDraftsRequestedAction = isActionFactory<FetchDraftsRequestedAction>(DraftsFetchInfoRequested);
export const isFetchDraftsSucceededAction = isActionFactory<FetchDraftsSucceededAction>(DraftsFetchInfoSucceeded);
export const isFetchDraftsFailedAction = isActionFactory<FetchDraftsFailedAction>(DraftsFetchInfoFailed);

export const isClearDraftsStateAction = isActionFactory<ClearDraftsStateAction>(ClearDraftsState);
export const isFetchDraftByParentIdFailedAction = isActionFactory<FetchDraftByReferenceIdFailedAction>(DraftFetchByReferenceIdFailed);
export const isFetchDraftByParentIdSucceededAction = isActionFactory<FetchDraftByReferenceIdSucceededAction>(DraftFetchByReferenceIdSucceeded);
export const isFetchDraftByParentIdRequestedAction = isActionFactory<FetchDraftByReferenceIdRequestedAction>(DraftFetchByReferenceIdRequested);
export const isDraftsInfoAvailable = isActionFactory<DraftsInfoAvailableAction>(DraftsInfoAvailable);
